import {
    VirtualPackageId, ProjectId, UserId, BlobId, PackageId, IntegrationId
} from '../ids';
import {compareVersionNumbers, getRandomUUID, isDefined} from '../../utils/utils';
import {DateTime} from '../base-types';
import {Platform, platformComparator} from '@webmate-web-core/shared/types/platform';

export interface ReleaseMetaInfo {
    id: string;
    uploaded_at: DateTime;
    version: string;
    short_version: string;
}

export interface VirtualPackage {
    id: VirtualPackageId;
    platform: Platform;
    creator: UserId;
    name: string;
    creationTime: DateTime;
    description: string;
    versionComment: string;
    index: number;
    amountOfVersions: number;
    packageSource: any;
    projectId: ProjectId;
    origPackageId: PackageId;
    origPackageType: string;
    origBlobId: BlobId;
    origMetaData: any;
    origProperties: any;
    origProvenanceInfo: any;
    metadata: any;
    instrumentedPackageId: PackageId;
    instrumentedPackageType?: string;
    instrumentedBlobId?: BlobId;
    instrumentedMetaData?: any;
    instrumentedProperties?: any;
    instrumenterVersion?: number;
}

export interface PackageInstallation {
    installationId: string;
    packageId: PackageId;
    packageType: string;
    state: 'finished';
}

export function createFinishedPackageInstallation(virtualPackage: VirtualPackage, instrumented: boolean): PackageInstallation {
    return {
        installationId: getRandomUUID(),
        packageId: instrumented ? virtualPackage.instrumentedPackageId : virtualPackage.origPackageId,
        packageType: instrumented ? (virtualPackage.instrumentedPackageType || virtualPackage.origPackageType) : virtualPackage.origPackageType,
        state: 'finished'
    };
}

let platforms = {
    ipa: new Platform('ios', 'generic'),
    apk: new Platform('android', 'generic'),
    unknown: new Platform('unknown', 'unknown')
};

/** helper function to make front-end specific modification to Devices */
export function packageFromBackendObject(d: any): VirtualPackage {
    if (d.name === undefined || d.name.length <= 0) {
        d.name = isDefined(d.origMetaData.name) ? d.origMetaData.name : "Unnamed app";
    }

    let platform: Platform = new Platform('unknown', 'unknown');

    switch (d.origPackageType) {
        case "apk":
            platform = new Platform('android', 'generic');
            break;
        case "ipa":
            platform = new Platform('ios', 'generic');
            break;
    }

    d.platform = platform;
    d.metadata = d.origMetaData;
    d.blobId = d.origBlobId;

    return d as VirtualPackage;
}

export function packageSortingFunction(a: VirtualPackage, b: VirtualPackage): number {
    if (a.platform === b.platform /* OS has priority */){
        return a.name.localeCompare(b.name);
    } else {
        return platformComparator(a.platform, b.platform);
    }
}

function getDecimalNumberFromVersionString(s: string): number {
    let substrings = s.split('.');
    if (substrings.length >= 2) {
        // The version has more dots and looks e.g. like 12.4.7 therefore discard the last positions
        s = substrings[0] + "." + substrings[1];
    }
    return Number(s);
}

// For reference: https://source.android.com/setup/start/build-numbers
let android_version_number_api_level_map: Record<string, string> = {
    '1.0': '1',
    '1.1': '2',
    '1.5': '3',
    '1.6': '4',
    '2.0': '5',
    '2.0.1': '6',
    '2.1': '7',
    '2.2': '8',
    '2.3': '9',
    '2.3.3': '10',
    '2.3.4': '10',
    '2.3.5': '10',
    '2.3.6': '10',
    '2.3.7': '10',
    '3.0': '11',
    '3.1': '12',
    '3.2': '13',
    '4.0': '14',
    '4.0.1': '14',
    '4.0.2': '14',
    '4.1': '16',
    '4.2': '17',
    '4.3': '18',
    '4.4': '19',
    '5.0': '21',
    '5.1': '22',
    '6.0': '23',
    '6.0.1': '23',
    '7.0': '24',
    '7.1': '25',
    '8.0': '26',
    '8.1': '27',
    '9': '28',
    '10': '29',
    '11': '30'
};

function findAndroidApiLevel(androidVersionNumber: string): string {
    let apiLevel = android_version_number_api_level_map[androidVersionNumber];
    if (!apiLevel) {
        let versionSubstrings = androidVersionNumber.split('.');
        if (versionSubstrings.length > 2) {
            return findAndroidApiLevel(versionSubstrings[0] + '.' + versionSubstrings[1]);
        } else {
            throw new Error(`Could not match android version number to android api level: ${androidVersionNumber}`)
        }
    }

    return apiLevel;
}

export function isPlatformCompatible(vp: VirtualPackage, platform: Platform): boolean {
    if (vp.platform?.platformType?.toLowerCase() !== platform.platformType.toLowerCase()) {
        return false;
    }
    if (platform.platformType.toLowerCase() == 'android') {
        // Match the platform version number to the api level number at first for android, in order to compare the minimum sdk version.
        try {
            let androidApiLevel = findAndroidApiLevel(platform.platformVersion);
            return compareVersionNumbers(androidApiLevel, vp.metadata.minSdk) >= 0;
        } catch (e) {
            //TODO: clean this up conceptually, we need to be able to be 'upward compatible' here
            return true;
        }
    } else if (platform.platformType.toLowerCase() == 'ios') {
        return compareVersionNumbers(platform.platformVersion, vp.metadata.minSdk) >= 0;
    } else {
        return false;
    }
}
